import React, { useMemo, useState } from 'react';
import { Table, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import { Link } from 'react-router-dom';
import './ChangeRequestsTable.less';
import { Result } from '../../../interfaces/changeRequest';
import { generateColumnProps } from '../../../utils/tableUtils';
import { LockOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { sortStrings } from '../../../utils/sort';

interface ChangeRequestsTableProps {
  className?: string;
  changeRequests: Result[];
  withAuthor: boolean;
  loading: boolean;
  disableSelection?: (cr: Result) => string | null;
  setSelectedRows: (selectedRows: Result[]) => void;
}

interface ChangeRequestRowData extends Result {
  disabled_selection_message: string | null;
}

const PAGINATION_DEFAULT_SIZE = 50;

const ChangeRequestsTable: React.FC<ChangeRequestsTableProps> = ({
  className,
  changeRequests,
  withAuthor,
  loading,
  disableSelection,
  setSelectedRows,
}) => {
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
  }>({
    current: 1,
    pageSize: PAGINATION_DEFAULT_SIZE,
  });

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || PAGINATION_DEFAULT_SIZE,
    });
  };

  const changeRequestRows = useMemo<ChangeRequestRowData[]>(() => {
    return changeRequests.map((cr) => ({
      ...cr,
      disabled_selection_message: disableSelection
        ? disableSelection(cr)
        : null,
    }));
  }, [changeRequests, disableSelection]);

  const columns: ColumnsType<ChangeRequestRowData> = [
    {
      title: 'CR Id',
      dataIndex: 'key',
      key: 'key',
      minWidth: 75,
      sorter: (a, b) => sortStrings(a?.key, b?.key),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      minWidth: 150,
      sorter: (a, b) => sortStrings(a?.name, b?.name),
      render: (text, record) => (
        <Link to={`/change-requests/${record.id}`} rel="noopener noreferrer">
          {text}
        </Link>
      ),
    },
    {
      title: 'External Id',
      dataIndex: 'external_id',
      key: 'external_id',
      minWidth: 100,
      sorter: (a, b) => sortStrings(a?.external_id, b?.external_id),
    },
    {
      title: 'Description',
      dataIndex: 'summary',
      key: 'summary',
      minWidth: 200,
    },
    ...(withAuthor
      ? [
          generateColumnProps<ChangeRequestRowData>({
            title: 'Author',
            dataIndex: 'cirra_user_id',
            key: 'cirra_user_id',
            sortable: true,
            dataType: 'string',
            minWidth: 100,
          }),
        ]
      : []),
    generateColumnProps<ChangeRequestRowData>({
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sortable: true,
      dataType: 'date',
      minWidth: 110,
    }),
    generateColumnProps<ChangeRequestRowData>({
      title: 'Modified At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sortable: true,
      dataType: 'date',
      minWidth: 110,
    }),
  ];

  const rowSelection: TableProps<ChangeRequestRowData>['rowSelection'] = {
    type: 'checkbox',
    getCheckboxProps: (cr) => {
      return {
        disabled: !!cr.disabled_selection_message,
      };
    },
    renderCell: (checked, cr, index, originNode) => {
      if (cr.disabled_selection_message) {
        return (
          <Tooltip title={cr.disabled_selection_message}>
            <LockOutlined className="change-request-row-lock-icon" />
          </Tooltip>
        );
      }
      return originNode;
    },
    onChange: (keys, rows) => {
      setSelectedRows(rows);
    },
  };

  return (
    <Table
      columns={columns}
      rowSelection={rowSelection}
      rowKey="id"
      loading={loading}
      className={classNames(
        'change-request-table',
        className,
        loading && 'table-spinner'
      )}
      dataSource={changeRequestRows}
      tableLayout="auto"
      pagination={{
        showSizeChanger: true,
        hideOnSinglePage: false,
        ...pagination,
      }}
      scroll={{ y: '100vh' }}
      onChange={handleTableChange}
    />
  );
};

export default ChangeRequestsTable;
